/**
 * Effective-price display (DNKADM-0086 U2) — a READ-ONLY "what will this date cost?"
 * view for ONE variation, rendered in the variation's expand row beside the date /
 * weekly / staff price editors.
 *
 * For a chosen date (and an optional staff selection) it asks the SERVER's resolver for
 * the effective price via {@link getEffectivePrice} — the server applies the precedence
 * `Date > Staff > Day-of-week > Base` (most-specific-wins, NOT additive). We never compute
 * that precedence client-side; we only render what the server resolves, with a `source`
 * label naming which rung won.
 *
 * **Forward-compatible (the surface is NOT locked).** DNKAPI-0168's effective-price
 * surface is still settling; `getEffectivePrice` returns `null` if it isn't live (404 /
 * absent / any failure). When that happens we **degrade to the variation's base price**
 * (a neutral "base price" indicator) rather than erroring.
 */
import { useEffect, useId, useState } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { getEffectivePrice, type EffectivePrice } from '@/api/services';
import { formatMoney } from '@/utils/v2Format';
import type { IdNameOption } from './AssignmentEditor';

export interface EffectivePriceViewProps {
  serviceId: string;
  variationId: string;
  /** The variation's base price — the degrade-to-base fallback when the surface is absent. */
  basePrice: number;
  /** The service's eligible staff (an optional staff selection for the resolved price). */
  staffOptions: IdNameOption[];
  /** The currency code, or null while it loads. */
  currency: string | null;
  /**
   * Bumped by the panel after every successful price write (DNKADM-0304 U3) —
   * re-resolves a pinned date so this preview and the editors beside it can't
   * disagree. Optional so standalone use keeps working.
   */
  priceVersion?: number;
}

/** Human label for the server-resolved precedence rung. */
const SOURCE_LABELS: Record<EffectivePrice['source'], string> = {
  date: 'date price',
  staff: 'coach price',
  weekly: 'day-of-week price',
  base: 'base price',
};

export function EffectivePriceView({
  serviceId,
  variationId,
  basePrice,
  staffOptions,
  currency,
  priceVersion = 0,
}: EffectivePriceViewProps) {
  const ids = useId();
  const [date, setDate] = useState('');
  const [staffId, setStaffId] = useState('');
  const [loading, setLoading] = useState(false);
  // The server's resolved price, or null while none is chosen / it degrades to base.
  const [resolved, setResolved] = useState<EffectivePrice | null>(null);

  useEffect(() => {
    if (date.trim() === '') {
      setResolved(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    void (async () => {
      // Never throws — a missing/404 surface resolves to null (we degrade to base below).
      const result = await getEffectivePrice(
        serviceId,
        variationId,
        date.trim(),
        staffId || undefined,
      );
      if (cancelled) return;
      setResolved(result);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
    // `priceVersion` re-resolves the same date after a sibling price write
    // (DNKADM-0304 U3) — the value itself is never read.
  }, [serviceId, variationId, date, staffId, priceVersion]);

  // Degrade to base when the server surface isn't live (forward-compatible).
  const effective: EffectivePrice = resolved ?? { price: basePrice, source: 'base' };
  const money = currency ? formatMoney(effective.price, currency) : effective.price.toFixed(2);

  const staffLabelId = `${ids}-staff-label`;

  return (
    <Box sx={{ mt: 1 }}>
      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
        Effective price for a date
      </Typography>
      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>
        Pick a date (and optionally a coach) to see the price the system will charge —
        resolved by the server (date &gt; coach &gt; weekday &gt; base).
      </Typography>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ alignItems: 'flex-start' }}>
        <TextField
          type="date"
          label="Date"
          size="small"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          sx={{ width: 180 }}
          slotProps={{
            inputLabel: { shrink: true },
            htmlInput: { 'aria-label': 'Effective price date' },
          }}
        />
        {staffOptions.length > 0 && (
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel id={staffLabelId}>Coach (optional)</InputLabel>
            <Select
              labelId={staffLabelId}
              label="Coach (optional)"
              value={staffId}
              onChange={(e) => setStaffId(e.target.value)}
              slotProps={{ input: { 'aria-label': 'Effective price coach' } }}
            >
              <MenuItem value="">
                <em>Any coach</em>
              </MenuItem>
              {staffOptions.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.name || s.id}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Stack>

      <Box role="status" aria-live="polite" sx={{ mt: 1.25, minHeight: 24 }}>
        {date.trim() === '' ? (
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Choose a date to see its effective price.
          </Typography>
        ) : loading ? (
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Checking…
          </Typography>
        ) : (
          <Typography variant="body2">
            On {date.trim()} this length costs{' '}
            <Box component="span" sx={{ fontWeight: 600 }}>
              {money}
            </Box>{' '}
            ({SOURCE_LABELS[effective.source]})
          </Typography>
        )}
      </Box>
    </Box>
  );
}
